import React, { Component } from 'react';
import ExcelJS from 'exceljs';
import { MobileTruckLoadContext } from './MobileTruckLoadContext';

function effClassLocal(eff) {
    if (eff >= 90) return 'good';
    if (eff >= 75) return 'ok';
    return 'low';
}

const CONSIGNEE_ICON_PATHS = '<path d="M21 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v3"></path><path d="M3 8h18v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><path d="M9 12l2 2 4-4"></path>';

function outcomeLabel(outcome) {
    if (outcome === 'valid') return 'Successful';
    if (outcome === 'duplicate') return 'Duplicate';
    if (outcome === 'wrong') return 'Wrong Truck';
    if (outcome === 'notfound') return 'Not Found';
    return outcome || '';
}

// Some HU codes come through as "123456.00" (upstream float coercion - e.g.
// SheetJS parses a numeric-looking cell in the original plan-data Excel
// upload as a JS float) - strip a trailing all-zero decimal so a genuinely
// numeric HU exports as a clean whole number. A plain digit string like that
// (no quotes forcing text, no decimal point) is exactly what Excel auto-
// detects as a real Number column on CSV import, instead of picking up a
// spurious ".00" or getting typed as text.
function formatHuForExport(hu) {
    var str = hu === null || hu === undefined ? '' : String(hu);
    return str.replace(/\.0+$/, '');
}

// e.g. 8125000ms -> "2 hr 15 min 25 sec" (drops the hr/min parts that are zero,
// so a short trip reads as "2 min 45 sec" or just "45 sec" instead of
// "0 hr 2 min 45 sec").
function formatDuration(ms) {
    var totalSeconds = Math.round(ms / 1000);
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;
    var parts = [];
    if (hours > 0) parts.push(hours + ' hr');
    if (hours > 0 || minutes > 0) parts.push(minutes + ' min');
    parts.push(seconds + ' sec');
    return parts.join(' ');
}

// The backend stores these timestamps with the real IST wall-clock digits
// under the UTC label (see loading_started_at/scanned_at etc. in
// lmconsigneesModel.js/lmConsigneesController.js) specifically so a developer
// reading the raw doc in a DB tool sees the actual time something happened
// with zero timezone math. Reading it back with any timezone-AWARE formatter
// (toLocaleString with a timeZone option, moment without .utc(), etc.) would
// convert those digits again and show the wrong time - this has to read the
// UTC-label components literally, via the UTC getters, and just print them.
function formatIST(date) {
    if (!date) return '';
    var pad = function (n) { return String(n).padStart(2, '0'); };
    var hours24 = date.getUTCHours();
    var period = hours24 >= 12 ? 'pm' : 'am';
    var hours12 = hours24 % 12 || 12;
    return pad(date.getUTCDate()) + '/' + pad(date.getUTCMonth() + 1) + '/' + date.getUTCFullYear() + ', '
        + pad(hours12) + ':' + pad(date.getUTCMinutes()) + ':' + pad(date.getUTCSeconds()) + ' ' + period;
}

// HU Scan Log's own "scanner" column - this is the Manual Input Scanner Name
// entered once per trip (already stamped onto every scan_log row as
// scanner_name), NOT the logged-in web app user's own account. Those are two
// different identities: staff_id/username is who was logged into the app;
// scanner_name is the physical scanner/operator name for this trip, which is
// what this report actually needs to show.
var HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
var HEADER_FONT = { bold: true, color: { argb: 'FFFFFFFF' } };

function styleHeaderRow(row) {
    row.eachCell(function (cell) {
        cell.fill = HEADER_FILL;
        cell.font = HEADER_FONT;
    });
}

function addKeyValueSheet(workbook, sheetName, headerLabels, kvRows) {
    var sheet = workbook.addWorksheet(sheetName);
    sheet.columns = [{ width: 22 }, { width: 28 }];
    styleHeaderRow(sheet.addRow(headerLabels));
    (kvRows || []).forEach(function (r) { sheet.addRow(r); });
}

// Builds one workbook with three separate sheets (tabs) - Trip Details, Load
// Progress, HU Scan Log - each with its own colored header row, instead of one
// flat CSV with blank-line-separated sections. tripDetailsRows/progressRows are
// arrays of [label, value] pairs; scanRows is the raw scan_log array (one row
// per real scan attempt, any outcome).
async function downloadLoadReportWorkbook(tripDetailsRows, progressRows, scanRows, filename) {
    var workbook = new ExcelJS.Workbook();
    addKeyValueSheet(workbook, 'Trip Details', ['Field', 'Value'], tripDetailsRows);
    addKeyValueSheet(workbook, 'Load Progress', ['Metric', 'Value'], progressRows);

    var scanSheet = workbook.addWorksheet('HU Scan Log');
    scanSheet.columns = [
        { width: 8 }, { width: 20 }, { width: 20 }, { width: 14 }, { width: 12 }, { width: 22 }, { width: 20 }
    ];
    // "Entry Mode" - Auto/Manual is how the code was captured (real
    // scanner/camera vs. typed by hand), not which of the app's two input
    // tabs was open - "Scan Type" read as asking the latter.
    styleHeaderRow(scanSheet.addRow(['Sl No', 'HU Id', 'Consignee', 'Result', 'Entry Mode', 'Scanned At', 'Scanner Name']));
    (scanRows || []).forEach(function (r, i) {
        scanSheet.addRow([
            i + 1,
            formatHuForExport(r.hu),
            r.consignee || '',
            outcomeLabel(r.outcome),
            r.is_auto ? 'Auto' : 'Manual',
            r.scanned_at ? formatIST(new Date(r.scanned_at)) : '',
            r.scanner_name || ''
        ]);
    });

    var buffer = await workbook.xlsx.writeBuffer();
    var blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// Ported from test.html's screenSummary - consignee-wise metrics, live from Scan or
// historical from a Dashboard trip's eye icon. Reads its opts from router location
// state (set by MobileScan.viewSummary / MobileDashboard.openHistoricalSummary).
export default class MobileSummary extends Component {
    static contextType = MobileTruckLoadContext;

    // Trip Details / Load Progress / Exceptions each collapse independently now,
    // instead of sharing one "Load Details" accordion - a user checking just the
    // exception counts shouldn't have to also scroll past the full trip detail
    // list to get there.
    state = { tripDetailsOpen: true, progressOpen: true, exceptionsOpen: true, finishing: false, exporting: false, rebuiltOpts: null };

    toggleSection = (key) => {
        this.setState((prev) => ({ [key]: !prev[key] }));
    };

    componentDidMount() {
        if (this.props.location.state) {
            // Router state survived the navigation - the remaining gap is live
            // mode's own actions (Finish Loading, Export Data), which act on the
            // LIVE ctx.session (Context state) directly, not on location.state.
            // ctx.session is separate in-memory state a refresh can wipe even when
            // the browser happens to keep location.state around.
            if (this.props.location.state.mode === 'live' && !this.context.session.loadVehicle) {
                this.rebuildFromIds();
            }
            return;
        }
        // A real refresh can wipe location.state too - whether it survives one at
        // all is browser-dependent, unlike the transactionId/tripNo now carried in
        // the URL itself (see MobileScan.viewSummary / MobileDashboard.
        // openHistoricalSummary, both of which push to a URL that includes them).
        // Rebuilding from those instead of bouncing to Input Plan/Dashboard is
        // exactly what actually survives a reload.
        this.rebuildFromIds();
    }

    // Fetches everything this screen needs (both to render, and for live mode's
    // own actions to work) straight from the backend by transactionId/tripNo, and
    // stores it in component state - not router state, not ctx.session, since
    // those are exactly the two things that don't reliably survive a refresh.
    // render() falls back to this.state.rebuiltOpts whenever location.state itself
    // is unavailable. See MobileTruckLoadContext.resumeSessionFromIds.
    rebuildFromIds = () => {
        var { transactionId, tripNo } = this.props.match.params;
        if (!transactionId || !tripNo) {
            // Truly nothing to go on - not even the URL knows what trip this was.
            this.props.history.replace('/lm-mobile/dashboard');
            return;
        }
        this.context.resumeSessionFromIds(transactionId, tripNo).then((opts) => {
            if (!opts) {
                this.props.history.replace('/lm-mobile/dashboard');
                return;
            }
            this.setState({ rebuiltOpts: opts });
        }).catch((e) => {
            console.log('resumeSessionFromIds failed', e);
            this.props.history.replace('/lm-mobile/dashboard');
        });
    };

    // Loading Start/End Time + Total Loading Time - sourced from
    // lm_truckload_trip_status (loading_started_at/loading_completed_at, see
    // markTripLoadingStarted/markTripLoadingCompleted), fetched fresh at export
    // time rather than threaded through session/dashboard state, so it's
    // accurate regardless of which screen the export was triggered from. Total
    // Loading Time is only included once there's actually a start AND an end -
    // a still-running trip has no end time yet to compute a duration from.
    getTripTimingRows = async (planId, tripNo) => {
        try {
            var statusRows = await this.context.fetchTripStatuses(planId);
            var statusRow = statusRows.find((r) => String(r.trip_no) === String(tripNo));
            if (!statusRow) return [];
            var startedAt = statusRow.loading_started_at ? new Date(statusRow.loading_started_at) : null;
            var completedAt = statusRow.loading_completed_at ? new Date(statusRow.loading_completed_at) : null;
            var rows = [
                ['Loading Start Time', startedAt ? formatIST(startedAt) : '—'],
                ['Loading End Time', completedAt ? formatIST(completedAt) : '—']
            ];
            if (startedAt && completedAt) {
                rows.push(['Total Loading Time', formatDuration(completedAt - startedAt)]);
            }
            return rows;
        } catch (e) {
            console.log('Could not fetch trip timing', e);
            return [];
        }
    };

    // Both reports below list every individual scan attempt for the trip (one row
    // per scan_log doc, in scan order) rather than a consignee-wise or
    // still-remaining-HU summary - that's what "download report" is actually for,
    // an auditable record of what was scanned, when, and by whom.
    exportLiveReport = () => {
        var s = this.context.session;
        this.setState({ exporting: true });
        Promise.all([
            this.context.fetchScanLogReal(s.planId, s.loadVehicle),
            this.getTripTimingRows(s.planId, s.loadVehicle)
        ]).then(([scanRows, timingRows]) => {
            var tripDetailsRows = [
                ['Load Date', this.context.formatDate(s.loadDate)],
                ['Plan Id', s.planId],
                ['Trip Type', s.loadType],
                ['Trip No', s.loadVehicle],
                ['Vehicle Number', s.vehicleNumber],
                ['LSP', s.lsp],
                ['Dock Number', s.dockNumber],
                ['Scanner Name', s.scannerName]
            ].concat(timingRows);
            var progressRows = [
                ['Expected Cartons', s.expectedHUs.length],
                ['Loaded', s.loaded],
                ['Remaining', s.remainingHUs.length],
                ['Auto Scans', s.autoScanCount],
                ['Manual Entry', s.manualScanCount],
                ['Duplicate', s.duplicateCount],
                ['Wrong Truck', s.wrongTruckCount],
                ['Not Found', s.notFoundCount],
                ['Progress', (s.expectedHUs.length > 0 ? Math.round((s.loaded / s.expectedHUs.length) * 100) : 0) + '%']
            ];
            downloadLoadReportWorkbook(tripDetailsRows, progressRows, scanRows, `LoadReport_${s.vehicleNumber || 'vehicle'}_${s.loadDate || 'date'}.xlsx`).then(() => {
                this.setState({ exporting: false });
            });
        }).catch((e) => {
            console.log('Could not fetch scan log for export', e);
            this.setState({ exporting: false });
            alert('Could not prepare the report - please try again.');
        });
    };

    downloadHistoricalReport = () => {
        var opts = this.props.location.state || this.state.rebuiltOpts;
        var trip = opts && opts.trip;
        // Button is disabled for this case too (see render) - guarded here as well
        // since a pending trip's report is an incomplete snapshot, not something
        // that should ever download regardless of how this gets triggered.
        if (!trip || !trip.completed) return;
        this.setState({ exporting: true });
        Promise.all([
            this.context.fetchScanLogReal(trip.planId, trip.tripNo),
            this.getTripTimingRows(trip.planId, trip.tripNo)
        ]).then(([scanRows, timingRows]) => {
            var tripDetailsRows = [
                ['Load Date', trip.dateLabel],
                ['Trip Type', trip.loadType],
                ['Vehicle', trip.vehicle],
                ['Trip No', trip.tripNo],
                ['LSP', trip.lsp],
                ['Dock Number', trip.dockNumber],
                ['Scanner Name', trip.scannerName]
            ].concat(timingRows);
            var progressRows = [
                ['Expected', trip.expected],
                ['Loaded', trip.actualLoaded],
                ['Remaining', Math.max(trip.expected - trip.actualLoaded, 0)],
                ['Auto Scans', trip.autoScans || 0],
                ['Manual Entry', trip.manualScans || 0],
                ['Duplicate', trip.duplicate],
                ['Wrong Truck', trip.wrongTruck],
                ['Not Found', trip.notFound],
                // efficiency (accuracy) is null on the trip object while it's still
                // running - report progress with that noted instead of "null%".
                [trip.completed ? 'Efficiency' : 'Progress', (trip.completed ? trip.efficiency : trip.progress) + '%' + (trip.completed ? '' : ' (in progress)')]
            ];
            downloadLoadReportWorkbook(tripDetailsRows, progressRows, scanRows, `LoadReport_${trip.vehicle || 'vehicle'}_${trip.dateLabel || 'date'}.xlsx`).then(() => {
                this.setState({ exporting: false });
            });
        }).catch((e) => {
            console.log('Could not fetch scan log for report', e);
            this.setState({ exporting: false });
            alert('Could not prepare the report - please try again.');
        });
    };

    finishLoading = () => {
        var ctx = this.context;
        var s = ctx.session;
        var dateStr = s.loadDate || new Date().toISOString().slice(0, 10);
        var expected = s.expectedHUs.length;
        var actualLoaded = s.loaded;
        var duplicate = s.duplicateCount;
        var wrongTruck = s.wrongTruckCount;
        var notFound = s.notFoundCount;
        var totalScans = expected + duplicate + wrongTruck + notFound;
        var efficiency = totalScans > 0 ? Math.round((expected / totalScans) * 100) : 100;

        this.setState({ finishing: true });
        // Flip loading_completed server-side before navigating away - Input Plan's
        // Trip No dropdown and the desktop upload slider both key off this flag to
        // show "fully loaded" instead of just "loading started" for this trip.
        ctx.finishTripLoadingReal(s.planId, s.loadVehicle).then(() => {
            ctx.recordCompletedTrip(dateStr, {
                vehicle: s.vehicleNumber || s.loadVehicle || 'Unknown',
                planId: s.planId,
                tripNo: s.loadVehicle,
                loadType: s.loadType,
                lsp: s.lsp,
                dockNumber: s.dockNumber,
                scannerName: s.scannerName,
                dateLabel: ctx.formatDate(dateStr),
                expected, actualLoaded, duplicate, wrongTruck, notFound, efficiency,
                manualScans: s.manualScanCount,
                autoScans: s.autoScanCount,
                consignees: ctx.computeConsigneeMetrics(s.expectedHUs, s.remainingHUs, s.manualByConsignee, s.autoByConsignee)
            });
            this.props.history.push({ pathname: '/lm-mobile/dashboard', state: { date: dateStr } });
        }).catch((e) => {
            console.log(e);
            this.setState({ finishing: false });
            alert('Could not mark this trip as finished - please try again.');
        });
    };

    render() {
        var opts = this.props.location.state || this.state.rebuiltOpts;
        if (!opts) {
            // Either about to redirect (rebuildFromIds found nothing to go on), or
            // still fetching it - a brief "Loading..." beats a blank flash while
            // resumeSessionFromIds's fetch is in flight.
            return <div className="mtl-screen-scroll" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</div>;
        }
        var totalExceptions = opts.duplicateTotal + opts.wrongTruckTotal + opts.notFoundTotal;
        var totalScans = opts.scannedTotal + totalExceptions;
        var exceptionsIconCls = totalScans === 0 ? 'mtl-icon-amber' : (totalExceptions > 0 ? 'mtl-icon-red' : 'mtl-icon-green');
        var isLive = opts.mode === 'live';
        // A historical view opened from a Dashboard card that's still pending (not
        // every eye-icon click is on a finished trip) is not completed either - only
        // trust opts.trip.completed, never assume historical implies done.
        var completed = !isLive && !!(opts.trip && opts.trip.completed);
        // Progress (scanned vs expected) is meaningful the whole time; efficiency
        // (scanned vs every scan ATTEMPT including duplicate/wrong-truck/not-found)
        // only means anything once the trip is actually done, since a running trip's
        // total attempt count isn't final yet - 5/5 loaded via 10 attempts (5
        // exceptions) is not 100% efficient even though it's 100% complete.
        var progress = opts.expectedTotal > 0 ? Math.round((opts.scannedTotal / opts.expectedTotal) * 100) : 0;
        var efficiency = totalScans > 0 ? Math.round((opts.scannedTotal / totalScans) * 100) : 0;
        var badgeValue = completed ? efficiency : progress;
        var effCls = effClassLocal(badgeValue);
        var allScanned = isLive && this.context.session.remainingHUs.length === 0;

        return (
            <>
                <div className="mtl-screen-scroll">
                        <div className="mtl-breadcrumb"><span className="mtl-crumb-parent">Truck Load Automation</span><span className="mtl-crumb-sep">&rsaquo;</span><b>{opts.breadcrumbLabel}</b></div>

                        {/* mtl-summary-details raises the accordion-body max-height cap
                            (see CSS) so a full field list never triggers its own internal
                            scrollbar - these are short, fixed-length lists, not something
                            that needs scroll-capping the way Missing HUs does. */}
                        <div className={"mtl-accordion mtl-summary-details" + (this.state.tripDetailsOpen ? " mtl-open" : "")} style={{ margin: '10px 12px 0' }}>
                            <button className="mtl-accordion-header" type="button" onClick={() => this.toggleSection('tripDetailsOpen')}>
                                <span className="mtl-detail-section-icon mtl-icon-blue">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="7" width="14" height="10" rx="1"></rect><path d="M15 10h4l3 3v4h-7z"></path><circle cx="6" cy="18.5" r="1.7"></circle><circle cx="17.5" cy="18.5" r="1.7"></circle></svg>
                                </span>
                                <span className="mtl-accordion-title">Trip Details</span>
                                <svg className="mtl-accordion-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                            </button>
                            <div className="mtl-accordion-body">
                                <div className="mtl-detail-kv">
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">Plan Id</span><span className="mtl-detail-kv-value">{opts.planId || '—'}</span></div>
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">Vehicle No</span><span className="mtl-detail-kv-value">{opts.vehicle || '—'}</span></div>
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">Load Date</span><span className="mtl-detail-kv-value">{opts.date || '—'}</span></div>
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">Trip Type</span><span className="mtl-detail-kv-value">{opts.loadType || '—'}</span></div>
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">Trip No</span><span className="mtl-detail-kv-value">{opts.tripNo || '—'}</span></div>
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">LSP</span><span className="mtl-detail-kv-value">{opts.lsp || '—'}</span></div>
                                    <div className="mtl-detail-kv-row"><span className="mtl-detail-kv-label">Dock Number</span><span className="mtl-detail-kv-value">{opts.dockNumber || '—'}</span></div>
                                    <div className="mtl-detail-kv-row mtl-last"><span className="mtl-detail-kv-label">Scanner Name</span><span className="mtl-detail-kv-value">{opts.scannerName || '—'}</span></div>
                                </div>
                            </div>
                        </div>

                        <div className={"mtl-accordion mtl-summary-details" + (this.state.progressOpen ? " mtl-open" : "")} style={{ margin: '10px 12px 0' }}>
                            <button className="mtl-accordion-header" type="button" onClick={() => this.toggleSection('progressOpen')}>
                                <span className="mtl-detail-section-icon mtl-icon-green">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v3"></path><path d="M3 8h18v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><path d="M9 12l2 2 4-4"></path></svg>
                                </span>
                                <span className="mtl-accordion-title">Load Progress</span>
                                <span className={"mtl-trip-eff-badge mtl-" + effCls}>{badgeValue}% {completed ? 'Eff' : 'Progress'}</span>
                                <svg className="mtl-accordion-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                            </button>
                            <div className="mtl-accordion-body">
                                <div className="mtl-progress-track" style={{ marginBottom: 12 }}><div className={"mtl-progress-fill mtl-fill-" + effCls} style={{ width: progress + '%' }}></div></div>
                                <div className="mtl-progress-stats-grid">
                                    <div className="mtl-progress-stat"><span className="mtl-progress-stat-val">{opts.expectedTotal}</span><span className="mtl-progress-stat-lbl">Expected</span></div>
                                    <div className="mtl-progress-stat"><span className="mtl-progress-stat-val">{opts.scannedTotal}</span><span className="mtl-progress-stat-lbl">Loaded</span></div>
                                    <div className="mtl-progress-stat"><span className="mtl-progress-stat-val">{opts.remainingTotal}</span><span className="mtl-progress-stat-lbl">Remaining</span></div>
                                    <div className="mtl-progress-stat"><span className="mtl-progress-stat-val">{opts.autoScans}</span><span className="mtl-progress-stat-lbl">Auto Scans</span></div>
                                    <div className="mtl-progress-stat mtl-progress-stat-wide"><span className="mtl-progress-stat-val">{opts.manualScans}</span><span className="mtl-progress-stat-lbl">Manual Entry</span></div>
                                </div>
                            </div>
                        </div>

                        <div className={"mtl-accordion mtl-summary-details" + (this.state.exceptionsOpen ? " mtl-open" : "")} style={{ margin: '10px 12px 0' }}>
                            <button className="mtl-accordion-header" type="button" onClick={() => this.toggleSection('exceptionsOpen')}>
                                <span className={"mtl-detail-section-icon " + exceptionsIconCls}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                                </span>
                                <span className="mtl-accordion-title">Exceptions</span>
                                <span className="mtl-accordion-count">{totalExceptions}</span>
                                <svg className="mtl-accordion-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                            </button>
                            <div className="mtl-accordion-body">
                                <div className="mtl-trip-flags">
                                    <span className={"mtl-trip-flag " + (opts.duplicateTotal > 0 ? 'mtl-orange' : 'mtl-neutral')}>{opts.duplicateTotal} Duplicate</span>
                                    <span className={"mtl-trip-flag " + (opts.wrongTruckTotal > 0 ? 'mtl-red' : 'mtl-neutral')}>{opts.wrongTruckTotal} Wrong Truck</span>
                                    <span className={"mtl-trip-flag " + (opts.notFoundTotal > 0 ? 'mtl-purple' : 'mtl-neutral')}>{opts.notFoundTotal} Not Found</span>
                                </div>
                            </div>
                        </div>

                        <p className="mtl-dash-trips-heading" style={{ margin: '12px 12px 10px', borderBottom: 'none', paddingBottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>Consignee-wise Metrics</span>
                            <span className="mtl-accordion-count">{(opts.consigneeRows || []).length}</span>
                        </p>
                        <div style={{ margin: '0 12px 12px' }}>
                            {(opts.consigneeRows || []).map((row, i) => {
                                var pctScanned = row.expected > 0 ? Math.round((row.scanned / row.expected) * 100) : 100;
                                var cls = effClassLocal(pctScanned);
                                return (
                                    <div className={"mtl-trip-card mtl-" + cls} key={i}>
                                        <div className="mtl-trip-card-head">
                                            <div className="mtl-trip-vehicle-wrap">
                                                <span className="mtl-trip-vehicle-icon">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: CONSIGNEE_ICON_PATHS }}></svg>
                                                </span>
                                                <span className="mtl-trip-vehicle">{row.consignee}</span>
                                            </div>
                                        </div>
                                        {/* Same tile style as the Load Progress accordion's stats grid,
                                            instead of this card's own divider-row style. */}
                                        <div className="mtl-progress-stats-grid mtl-progress-stats-grid-3">
                                            <div className="mtl-progress-stat"><span className="mtl-progress-stat-val">{row.expected}</span><span className="mtl-progress-stat-lbl">Expected</span></div>
                                            <div className="mtl-progress-stat"><span className="mtl-progress-stat-val">{row.scanned}</span><span className="mtl-progress-stat-lbl">Loaded</span></div>
                                            <div className="mtl-progress-stat"><span className="mtl-progress-stat-val">{row.remaining}</span><span className="mtl-progress-stat-lbl">Remaining</span></div>
                                        </div>
                                        <div className="mtl-trip-flags mtl-trip-flags-consignee">
                                            <span className="mtl-trip-flag mtl-neutral">{row.manual || 0} Manual Entry</span>
                                            <span className="mtl-trip-flag mtl-neutral">{row.auto || 0} Scans</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {isLive ? (
                        <div className="mtl-actions2">
                            {!allScanned && <div className="mtl-error" style={{ margin: '0 0 8px' }}>Complete all scans before exporting data or finishing loading.</div>}
                            <div className="mtl-bottom-row">
                                <button className="mtl-export-btn" disabled={!allScanned || this.state.finishing || this.state.exporting} onClick={this.exportLiveReport}>{this.state.exporting ? 'Preparing...' : 'Export Data'}</button>
                                <button className={"mtl-finish-btn" + (allScanned ? " mtl-active" : "")} disabled={!allScanned || this.state.finishing} onClick={this.finishLoading}>{this.state.finishing ? 'Finishing...' : 'Finish Loading'}</button>
                            </div>
                        </div>
                ) : (
                    <div className="mtl-actions2">
                        {/* A pending trip's report is an incomplete/still-moving snapshot -
                            same reasoning as live mode's own allScanned gate above, just
                            checked against trip.completed instead since there's no live
                            session here to read remainingHUs from. */}
                        {!completed && <div className="mtl-error" style={{ margin: '0 0 8px' }}>Report is available once this trip is completed.</div>}
                        <div className="mtl-bottom-row">
                            <button className="mtl-export-btn" disabled={!completed || this.state.exporting} onClick={this.downloadHistoricalReport}>{this.state.exporting ? 'Preparing...' : 'Download Report'}</button>
                        </div>
                    </div>
                )}
            </>
        );
    }
}
